/**
 * F41 (rev-11 follow-up, D19) — admin create-tournament dialog.
 *
 * Creates a DRAFT tournament on behalf of a chosen organizer via
 * `POST /admin/tournaments` (`tournaments:create` — super_admin +
 * content_moderator; bypasses the Pro-tier gate + waives the listing fee at
 * publish, with a mandatory audited reason — M13). Pragmatic single-page
 * dialog carrying the minimum `AdminCreateTournamentInput` payload the backend
 * schema demands: organizer + reason + title/sport/dates/registration window.
 *
 * The organizer picker lists APPROVED organizer partners from
 * `GET /admin/organizers?status=approved` (the F41 §18 model — a tournament is
 * always organized by a b2b_account with an approved organizer profile, or a
 * Pro venue/club account; the admin-create path targets the former).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Select, Textarea, TextInput } from '@bravemove/ui-web';
import {
  AdminCreateTournamentInputSchema,
  SPORT_NAME_VALUES,
  type AdminCreateTournamentInput,
  type OrganizerProfileWithAccount,
  type TournamentDetail,
} from '@bravemove/types';
import { adminTournamentsApi } from '../../api/tournaments';
import { translateApiError } from '../../auth/errors';

export interface CreateTournamentDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called with the created draft (the page navigates to its detail). */
  readonly onCreated: (created: TournamentDetail) => void;
}

function humaniseSport(sport: string): string {
  return sport.replace(/-/g, ' ');
}

/** datetime-local value → ISO-8601 with offset (empty → null). */
function toIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function CreateTournamentDialog({ open, onClose, onCreated }: CreateTournamentDialogProps) {
  const { t } = useI18n();

  const [organizers, setOrganizers] = useState<readonly OrganizerProfileWithAccount[]>([]);
  const [organizersLoading, setOrganizersLoading] = useState(false);
  const [organizerId, setOrganizerId] = useState('');
  const [reason, setReason] = useState('');
  const [title, setTitle] = useState('');
  const [sportName, setSportName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [registrationOpenAt, setRegistrationOpenAt] = useState('');
  const [registrationCloseAt, setRegistrationCloseAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrganizers = useCallback(async () => {
    setOrganizersLoading(true);
    try {
      const res = await adminTournamentsApi.listOrganizers({ status: 'approved', limit: 100 });
      setOrganizers(res.data);
    } catch {
      // Best-effort — an empty picker keeps the form valid-but-unsubmittable.
      setOrganizers([]);
    } finally {
      setOrganizersLoading(false);
    }
  }, []);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setOrganizerId('');
    setReason('');
    setTitle('');
    setSportName('');
    setDescription('');
    setStartDate('');
    setEndDate('');
    setRegistrationOpenAt('');
    setRegistrationCloseAt('');
    setError(null);
    void loadOrganizers();
  }, [open, loadOrganizers]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const payload = useMemo<AdminCreateTournamentInput | null>(() => {
    const start = toIso(startDate);
    const end = toIso(endDate);
    const regOpen = toIso(registrationOpenAt);
    const regClose = toIso(registrationCloseAt);
    if (!start || !end || !regOpen || !regClose) return null;
    const candidate = {
      organizerB2bAccountId: organizerId,
      reason: reason.trim(),
      tournament: {
        title: title.trim(),
        sportName,
        ...(description.trim() ? { description: description.trim() } : {}),
        startDate: start,
        endDate: end,
        registrationOpenAt: regOpen,
        registrationCloseAt: regClose,
        prizePoolCurrency: 'USD',
        inviteOnly: false,
      },
    };
    const parsed = AdminCreateTournamentInputSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
  }, [
    organizerId,
    reason,
    title,
    sportName,
    description,
    startDate,
    endDate,
    registrationOpenAt,
    registrationCloseAt,
  ]);

  // Client-side mirror of the create-time cross-field invariants (the base
  // schema is un-refined; the service enforces them — fail fast here).
  const datesCoherent = useMemo(() => {
    const start = toIso(startDate);
    const end = toIso(endDate);
    const regOpen = toIso(registrationOpenAt);
    const regClose = toIso(registrationCloseAt);
    if (!start || !end || !regOpen || !regClose) return true; // incomplete ≠ incoherent
    return (
      new Date(end).getTime() >= new Date(start).getTime() &&
      new Date(regOpen).getTime() < new Date(regClose).getTime() &&
      new Date(regClose).getTime() <= new Date(start).getTime()
    );
  }, [startDate, endDate, registrationOpenAt, registrationCloseAt]);

  const valid = payload !== null && datesCoherent;

  const submit = async (): Promise<void> => {
    if (!payload) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await adminTournamentsApi.createTournament(payload);
      onCreated(created);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const sportOptions = useMemo(
    () => [
      {
        value: '',
        label: t('admin:tournament.createDialog.sportPlaceholder', {
          defaultValue: 'Pick a sport',
        }),
      },
      ...SPORT_NAME_VALUES.map((s) => ({ value: s, label: humaniseSport(s) })),
    ],
    [t],
  );

  const organizerOptions = useMemo(
    () => [
      {
        value: '',
        label: organizersLoading
          ? t('admin:tournament.createDialog.organizersLoading', { defaultValue: 'Loading…' })
          : t('admin:tournament.createDialog.organizerPlaceholder', {
              defaultValue: 'Pick an organizer',
            }),
      },
      ...organizers.map((o) => ({
        value: o.b2bAccountId,
        label: `${o.accountDisplayName} (${o.organizerType})`,
      })),
    ],
    [organizers, organizersLoading, t],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:tournament.createDialog.title', { defaultValue: 'Create tournament' })}
      description={t('admin:tournament.createDialog.body', {
        defaultValue:
          'Creates a DRAFT on behalf of the chosen organizer (D19 — the tier gate is bypassed and the listing fee waived at publish). Audited.',
      })}
      size="lg"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="create-tournament-cancel"
          >
            {t('admin:tournament.createDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="create-tournament-submit"
          >
            {t('admin:tournament.createDialog.confirm', { defaultValue: 'Create draft' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="create-tournament-dialog">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {!datesCoherent ? (
          <Banner tone="warning">
            {t('admin:tournament.createDialog.datesIncoherent', {
              defaultValue: 'Dates must satisfy: registration opens < closes ≤ start ≤ end.',
            })}
          </Banner>
        ) : null}

        <FormField
          label={t('admin:tournament.createDialog.organizerLabel', { defaultValue: 'Organizer' })}
          required
        >
          <Select
            value={organizerId}
            onChange={(e) => setOrganizerId(e.target.value)}
            options={organizerOptions}
            data-testid="create-tournament-organizer"
          />
        </FormField>

        <FormField
          label={t('admin:tournament.createDialog.titleLabel', { defaultValue: 'Title' })}
          required
        >
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            data-testid="create-tournament-title"
          />
        </FormField>

        <FormField
          label={t('admin:tournament.createDialog.sportLabel', { defaultValue: 'Sport' })}
          required
        >
          <Select
            value={sportName}
            onChange={(e) => setSportName(e.target.value)}
            options={sportOptions}
            data-testid="create-tournament-sport"
          />
        </FormField>

        <FormField
          label={t('admin:tournament.createDialog.descriptionLabel', {
            defaultValue: 'Description',
          })}
        >
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={2000}
            data-testid="create-tournament-description"
          />
        </FormField>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            label={t('admin:tournament.createDialog.startLabel', { defaultValue: 'Starts' })}
            required
          >
            <TextInput
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="create-tournament-start"
            />
          </FormField>
          <FormField
            label={t('admin:tournament.createDialog.endLabel', { defaultValue: 'Ends' })}
            required
          >
            <TextInput
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="create-tournament-end"
            />
          </FormField>
          <FormField
            label={t('admin:tournament.createDialog.regOpenLabel', {
              defaultValue: 'Registration opens',
            })}
            required
          >
            <TextInput
              type="datetime-local"
              value={registrationOpenAt}
              onChange={(e) => setRegistrationOpenAt(e.target.value)}
              data-testid="create-tournament-reg-open"
            />
          </FormField>
          <FormField
            label={t('admin:tournament.createDialog.regCloseLabel', {
              defaultValue: 'Registration closes',
            })}
            required
          >
            <TextInput
              type="datetime-local"
              value={registrationCloseAt}
              onChange={(e) => setRegistrationCloseAt(e.target.value)}
              data-testid="create-tournament-reg-close"
            />
          </FormField>
        </div>

        <FormField
          label={t('admin:tournament.createDialog.reasonLabel', { defaultValue: 'Reason' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('admin:tournament.createDialog.reasonPlaceholder', {
              defaultValue: 'Why is an admin creating this tournament?',
            })}
            rows={3}
            maxLength={500}
            data-testid="create-tournament-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}

export default CreateTournamentDialog;
